/** Shown when a number is missing. Never a dash: clients read "—" as an error. */
export const NOT_AVAILABLE = "Not set";

const usd0 = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const usd2 = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function money(amount: number | null | undefined, opts: { cents?: boolean; exact?: boolean } = {}): string {
  if (amount == null || Number.isNaN(amount)) return NOT_AVAILABLE;
  const value = opts.cents ? amount / 100 : amount;
  return (opts.exact ? usd2 : usd0).format(value);
}

export function percent(value: number | null | undefined, digits = 0): string {
  if (value == null || Number.isNaN(value)) return NOT_AVAILABLE;
  return `${value.toFixed(digits)}%`;
}

/**
 * A date-only value ("2026-10-01") is a calendar day, not an instant. Parsing
 * it with new Date() treats it as UTC midnight, which shows as the previous
 * day anywhere west of Greenwich. So calendar days are built as local dates.
 */
export function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function longDate(value: string | null | undefined): string {
  const d = parseDate(value);
  return d ? d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "";
}

/** "Sep 30" for dates within the next 11 months, else with the year. */
export function friendlyDate(value: string | null | undefined, now = new Date()): string {
  const d = parseDate(value);
  if (!d) return "";
  const months = (d.getFullYear() - now.getFullYear()) * 12 + d.getMonth() - now.getMonth();
  return months >= -1 && months <= 11 ? d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) : shortDate(value);
}

export function shortDate(value: string | null | undefined): string {
  const d = parseDate(value);
  return d ? d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
}

export function timeOfDay(value: string | null | undefined): string {
  const d = parseDate(value);
  return d ? d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "";
}
